import { useEffect, useState } from 'react';
import type { TagResponse } from '../api';
import {
  useAddFragment,
  useAttachTag,
  useDetachTag,
  usePurgeSnippet,
  useRemoveFragment,
  useRestoreSnippet,
  useSetArchived,
  useSnippet,
  useToggleFavorite,
  useUpdateFragment,
  useUpdateSnippet,
} from './queries';
import { FragmentEditor } from './FragmentEditor';
import { defaultFragmentLanguage, defaultFragmentName } from './fragmentNaming';
import { TagInput } from '../components/TagInput';
import { ShareDialog } from '../components/ShareDialog';
import { Banner, Button, EmptyState, Spinner } from '../components/ui';
import { errorMessage } from '../api/problems';

interface SnippetDetailProps {
  snippetId: string;
  availableTags: TagResponse[];
  onDeleted: () => void;
  onDelete: (id: string) => void;
}

export function SnippetDetail({
  snippetId,
  availableTags,
  onDeleted,
  onDelete,
}: SnippetDetailProps) {
  const { data: snippet, isPending, isError, error } = useSnippet(snippetId);

  const updateSnippet = useUpdateSnippet(snippetId);
  const toggleFavorite = useToggleFavorite(snippetId);
  const setArchived = useSetArchived(snippetId);
  const addFragment = useAddFragment(snippetId);
  const updateFragment = useUpdateFragment(snippetId);
  const removeFragment = useRemoveFragment(snippetId);
  const attachTag = useAttachTag(snippetId);
  const detachTag = useDetachTag(snippetId);
  const restore = useRestoreSnippet();
  const purge = usePurgeSnippet();

  const [activeFragmentId, setActiveFragmentId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sharing, setSharing] = useState(false);

  // Fragment names as they are being typed, so a tab renames immediately instead of waiting for
  // autosave and a refetch to come back around.
  const [draftNames, setDraftNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (snippet) {
      setTitle(snippet.title);
      setDescription(snippet.description ?? '');
    }
  }, [snippet?.id, snippet?.title, snippet?.description]);

  /**
   * Title and description go together, because the endpoint takes both — sending one means sending
   * the current value of the other, which is the contract rather than a shortcut.
   */
  function saveDetails() {
    if (!snippet || !canEdit) {
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const nextDescription = trimmedDescription === '' ? null : trimmedDescription;

    // An empty title is a slip, not an intention: there is nothing to call the snippet afterwards.
    if (trimmedTitle === '') {
      setTitle(snippet.title);
      return;
    }

    if (trimmedTitle === snippet.title && nextDescription === snippet.description) {
      return;
    }

    updateSnippet.mutate({ title: trimmedTitle, description: nextDescription });
  }

  // Keep a valid tab selected as fragments come and go.
  useEffect(() => {
    if (!snippet) {
      return;
    }

    const stillThere = snippet.fragments.some((fragment) => fragment.id === activeFragmentId);
    if (!stillThere) {
      setActiveFragmentId(snippet.fragments[0]?.id ?? null);
    }
  }, [snippet, activeFragmentId]);

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Loading snippet" />
      </div>
    );
  }

  if (isError || !snippet) {
    return (
      <div className="p-6">
        <Banner tone="error">{errorMessage(error, 'That snippet could not be loaded.')}</Banner>
      </div>
    );
  }

  const activeFragment =
    snippet.fragments.find((fragment) => fragment.id === activeFragmentId) ?? snippet.fragments[0];

  // The API tells us what this caller may do, so the UI hides what it would refuse rather than
  // offering controls that fail.
  const isTrashed = snippet.deletedAt !== null;
  const canEdit = !isTrashed && (snippet.yourAccess === 'Write' || snippet.yourAccess === 'Manage');
  const canManage = !isTrashed && snippet.yourAccess === 'Manage';

  // Only personal snippets are shared per-snippet; team ones are governed by membership.
  const canShare = canManage && snippet.teamId === null;

  return (
    <section className="flex h-full min-w-0 flex-col" aria-label="Snippet">
      <header className="flex flex-col gap-3 border-b border-border-subtle px-4 py-3">
        {isTrashed && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md
            border border-border-subtle bg-surface-raised px-3 py-2">
            <span className="text-sm text-text-muted">
              In the trash. Editing is off until you put it back.
            </span>

            {snippet.yourAccess === 'Manage' && (
              <span className="flex items-center gap-2">
                <Button
                  variant="primary"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(snippet.id)}
                >
                  {restore.isPending ? 'Restoring…' : 'Restore'}
                </Button>

                <Button
                  variant="danger"
                  disabled={purge.isPending}
                  onClick={() => {
                    // No undo behind this one, so ask.
                    if (window.confirm(`Delete "${snippet.title}" for good? This cannot be undone.`)) {
                      purge.mutate(snippet.id, { onSuccess: onDeleted });
                    }
                  }}
                >
                  {purge.isPending ? 'Deleting…' : 'Delete for good'}
                </Button>
              </span>
            )}
          </div>
        )}

        {snippet.isShared && (
          <p className="text-xs text-text-muted">
            {snippet.teamId
              ? 'In a team library — every member can see and edit this.'
              : snippet.yourAccess === 'Read'
                ? 'Shared with you, read only.'
                : 'Shared with you. You can edit it, but not delete or re-share it.'}
          </p>
        )}

        <div className="flex items-start gap-2">
          <input
            value={title}
            disabled={!canEdit}
            aria-label="Snippet title"
            onChange={(event) => setTitle(event.target.value)}
            onBlur={saveDetails}
            className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1
              text-lg font-semibold text-text hover:border-border-subtle focus:border-border-subtle
              disabled:opacity-60"
          />

          <Button
            variant="ghost"
            aria-pressed={snippet.isFavorite}
            aria-label={snippet.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
            disabled={!canEdit}
            onClick={() => toggleFavorite.mutate(!snippet.isFavorite)}
          >
            <span aria-hidden="true" className={snippet.isFavorite ? 'text-accent' : ''}>
              {snippet.isFavorite ? '★' : '☆'}
            </span>
          </Button>

          <Button
            variant="ghost"
            disabled={!canEdit}
            onClick={() => setArchived.mutate(!snippet.isArchived)}
          >
            {snippet.isArchived ? 'Unarchive' : 'Archive'}
          </Button>

          {canShare && (
            <Button variant="ghost" onClick={() => setSharing(true)}>
              Share
            </Button>
          )}

          {canManage && (
            <Button
              variant="ghost"
              onClick={() => {
                onDelete(snippet.id);
                onDeleted();
              }}
            >
              Delete
            </Button>
          )}
        </div>

        <input
          value={description}
          disabled={!canEdit}
          aria-label="Snippet description"
          placeholder="Add a description…"
          onChange={(event) => setDescription(event.target.value)}
          onBlur={saveDetails}
          className="w-full rounded-md border border-transparent bg-transparent px-2 py-0.5 text-xs
            text-text-muted placeholder:text-text-faint hover:border-border-subtle
            focus:border-border-subtle disabled:opacity-60"
        />

        <TagInput
          attached={snippet.tags}
          available={availableTags}
          disabled={!canEdit}
          onAttach={(name) => attachTag.mutate(name)}
          onDetach={(tagId) => detachTag.mutate(tagId)}
        />
      </header>

      <div className="flex items-center gap-1 overflow-x-auto border-b border-border-subtle px-2">
        <div role="tablist" aria-label="Fragments" className="flex min-w-0 items-center gap-1">
          {snippet.fragments.map((fragment) => (
            <button
              key={fragment.id}
              role="tab"
              type="button"
              aria-selected={fragment.id === activeFragment?.id}
              onClick={() => setActiveFragmentId(fragment.id)}
              className={`shrink-0 border-b-2 px-3 py-2 font-mono text-xs transition-colors
                ${
                  fragment.id === activeFragment?.id
                    ? 'border-accent text-text'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
            >
              {draftNames[fragment.id] ?? fragment.name}
            </button>
          ))}
        </div>

        {canEdit && (
          <Button
            variant="ghost"
            className="shrink-0"
            onClick={() => {
              // The title as typed, not the saved copy: renaming a snippet and immediately adding
              // a fragment should use the name on screen, not the one the server last heard.
              const name = defaultFragmentName(title, snippet.fragments.length);

              addFragment.mutate(
                { name, language: defaultFragmentLanguage(name), content: '' },
                { onSuccess: (fragment) => setActiveFragmentId(fragment.id) },
              );
            }}
          >
            + Fragment
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {activeFragment ? (
          <FragmentEditor
            key={activeFragment.id}
            fragment={activeFragment}
            canEdit={canEdit}
            onSave={async (input) => {
              await updateFragment.mutateAsync({ fragmentId: activeFragment.id, ...input });
            }}
            onNameChange={(name) =>
              setDraftNames((names) => ({ ...names, [activeFragment.id]: name }))
            }
            onRemove={() => removeFragment.mutate(activeFragment.id)}
          />
        ) : (
          <EmptyState title="No fragments yet">
            A snippet can hold several files or tabs. Add one to start writing.
          </EmptyState>
        )}
      </div>

      {sharing && (
        <ShareDialog
          snippetId={snippet.id}
          title={snippet.title}
          onClose={() => setSharing(false)}
        />
      )}
    </section>
  );
}
